import { useEffect, useState } from "react";
import {
    FaUsers, FaUserCheck, FaUserTimes, FaArrowRight,
    FaArrowLeft, FaSearch, FaDownload, FaClipboardCheck,
    FaFilter, FaEye, FaChevronUp, FaChevronDown, FaChartBar,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { sendRequest } from "../../../utils/api";
import { formatDate, formatDuration, formatTime } from "../../../utils/date";
import FullPageSpinner from "../../../components/FullPageSpinner";
import { FaFilterCircleXmark } from "react-icons/fa6";
import Site from "../../../models/Site";
import { capitalizeFirst, capitalizeWords } from "../../../utils/string";
import { exportToExcel, getFileName } from "../../../utils/helper-function";
import { FilterChip, FilterMenuItem, FilterOption, getDateLabel } from "../../../components/FilterComponents";

interface StatSummary {
    id: string;
    title: string;
    value: number;
    icon: any;
    bg: string
};


export default function AttendanceLog() {
    const navigate = useNavigate();

    const chevUpIcon = FaChevronUp({});
    const chevDownIcon = FaChevronDown({});

    const [showAttendance, setShowAttendance] = useState(true);
    const [showSummary, setShowSummary] = useState(true);
    const [loading, setLoading] = useState(false);

    const [attendanceList, setAttendanceList] = useState([]);

    const [filter, setFilter] = useState({
        date: "today", department: "all", site: "all", status: "all"
    });

    const [showFilters, setShowFilters] = useState(false);
    const [activeFilterMenu, setActiveFilterMenu] = useState<string | null>(null);
    const [search, setSearch] = useState("");

    const [dateRange, setDateRange] = useState({
        from: "",
        to: "",
    });
    const [stats, setStats] = useState<StatSummary[]>([]);
    const [sites, setSites] = useState<Site[]>([])

    const hasActiveFilters =
        filter.department !== "all" ||
        filter.site !== "all" ||
        filter.status !== "all" ||
        dateRange.from || dateRange.to;

    type Key = "date" | "department" | "site" | "status";
    const getAttendance = async (filters?: Partial<Record<Key, string>>) => {
        // if (key && value) filter[key] = value;
        const params = new URLSearchParams({ ...filter, ...dateRange, ...filters, }).toString();

        const {
            attendanceList, totalAbsentee, totalAttendee
        } = await sendRequest("GET", `/attendance/history?${params}`);

        const summaryCards = [
            {
                id: "present",
                title: "Present Today",
                value: totalAttendee,
                icon: FaUserCheck({ className: "text-2xl text-green-600" }),
                bg: "bg-green-50",
            },
            {
                id: "absent",
                title: "Absent Today",
                value: totalAbsentee,
                icon: FaUserTimes({ className: "text-2xl text-red-600" }),
                bg: "bg-red-50",
            },
        ];

        setStats(summaryCards);

        setAttendanceList(attendanceList.map((att: any) => ({
            id: att.id,
            createdAt: att.createdAt,
            employeePhotoUrl: att?.user?.photoUrl,
            employeeName: `${att?.user?.firstName} ${att?.user?.lastName}`,
            employeeId: att?.user?.staffId,
            department: att?.user?.portfolio,
            site: att?.site?.name,
            clockIn: att.clockIn,
            clockOut: att.clockOut,
            siteAddress: att.site?.address,
            // hours: "5h 40m",
            distance: att.distance + "m",
            // status: "Late",
        })));

    }


    useEffect(() => {
        const getSites = async () => {
            const sites = await sendRequest("GET", "/sites");
            setSites(sites);
        }

        try {
            getSites();
            getAttendance();
        } catch (err: any) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, []);


    const showAttendantDetails = (att: any) => {
        localStorage.setItem("attendance", JSON.stringify(att));
        navigate(`/attendances/${att.id}`)
    }



    const searchStaff = attendanceList.filter((att: any) =>
        att.employeeName?.toLowerCase().includes(search.toLowerCase())
    );

    const exportExcel = () => {
        setLoading(true);

        try {
            // Export only the currently filtered/searched attendance records
            const recordsToExport = attendanceList.filter((att: any) =>
                att.employeeName?.toLowerCase().includes(search.toLowerCase())
            );

            const fileName = getFileName("attendance-report");

            const formatList = recordsToExport.map((att: any, idx: number) => ({
                "S/N": idx + 1,
                "Date": formatDate(att.createdAt),
                "User ID": capitalizeWords(att.employeeId),
                "Staff Name": capitalizeFirst(att.employeeName),
                "Portfolio": capitalizeFirst(att.department),
                "Assigned Site": capitalizeFirst(att.site),
                "Site Address": capitalizeFirst(att.siteAddress),
                "Clock-In": att.clockIn ? formatTime(att.clockIn) : "---",
                "Clock-Out": att.clockOut ? formatTime(att.clockOut) : "---",
                "Hour Worked":
                    att.clockOut && att.clockIn
                        ? formatDuration(att.clockIn, att.clockOut)
                        : "Ongoing",
                "Distance": att.distance,
            }));

            exportToExcel(fileName, formatList);
        } catch (error) {
            console.error("Failed to export attendance:", error);
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="min-h-screen bg-gray-50 p-4 lg:p-6">

            {/* Header */}
            <div className="mb-8 flex flex-col gap-4">
                <FullPageSpinner show={loading} />
                <div className="space-y-5">

                    {/* Header */}
                    <div className="flex items-center gap-3">

                        {/* Title */}
                        <div className="min-w-0 flex-1">

                            <div className="flex items-start gap-3">
                                <button
                                    type="button"
                                    onClick={() => navigate("/expenses")}
                                    className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                                >
                                    {FaArrowLeft({
                                        size: 13,
                                        className: "",
                                    })}
                                </button>

                                <div>
                                    <h1 className="text-2xl font-bold text-slate-800">
                                        Attendance Logs
                                    </h1>

                                    <p className="mt-1 text-xs text-slate-400">
                                        Monitor staff attendance, clock-ins and site activity.
                                    </p>
                                </div>
                            </div>


                        </div>

                        {/* Export */}
                        <button
                          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
                            title="Export Excel"
                            onClick={exportExcel}
                        >

                            {FaDownload({})}

                            <span className="hidden sm:inline">
                                Export Excel
                            </span>
                        </button>
                    </div>

                    {/* Search & Filter */}
                    <div className="relative w-full">
                        {/* Search Icon */}
                        <span className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-gray-400">
                            {FaSearch({})}
                        </span>

                        {/* Search input / selected filters */}
                        <div className="flex min-h-[46px] w-full items-center gap-2 rounded-xl border border-gray-200 bg-white py-1.5 pl-11 pr-14 text-sm transition focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100">

                            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                                {filter.date !== "today" && (
                                    <FilterChip
                                        label={getDateLabel(filter.date, dateRange)}
                                        onRemove={() => {
                                            // alert(5)
                                            setFilter(prev => ({ ...prev, date: "today" }));
                                            getAttendance({ date: "today" });
                                        }}
                                    />
                                )}

                                {filter.site !== "all" && (
                                    <FilterChip
                                        label={filter.site}
                                        onRemove={() => {
                                            setFilter(prev => ({ ...prev, site: "all" }));
                                            getAttendance({ "site": "all" });
                                        }}
                                    />
                                )}

                                {filter.department !== "all" && (
                                    <FilterChip
                                        label={filter.department}
                                        onRemove={() =>
                                            setFilter(prev => ({ ...prev, department: "all" }))
                                        }
                                    />
                                )}

                                {filter.status !== "all" && (
                                    <FilterChip
                                        label={filter.status}
                                        onRemove={() =>
                                            setFilter(prev => ({ ...prev, status: "all" }))
                                        }
                                    />
                                )}

                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder={
                                        filter.date === "today" &&
                                            filter.site === "all" &&
                                            filter.department === "all" &&
                                            filter.status === "all"
                                            ? "Search staff..."
                                            : "Search staff..."
                                    }
                                    className="min-w-[120px] flex-1 border-0 bg-transparent py-1 outline-none placeholder:text-gray-400"
                                />
                            </div>
                        </div>

                        {/* Filter Button */}
                        <button
                            type="button"
                            onClick={() => {
                                setShowFilters(prev => !prev);
                                setActiveFilterMenu(null);
                            }}
                            className={`absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg transition ${showFilters
                                ? "bg-indigo-50 text-indigo-600"
                                : "text-gray-500 hover:bg-gray-100 hover:text-indigo-600"
                                }`}
                            title="Filter"
                        >
                            {FaFilter({})}
                        </button>

                        {/* Filter Menu */}
                        {showFilters && (
                            <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-72 overflow-visible rounded-xl border border-gray-200 bg-white p-2 shadow-xl">

                                {/* Date */}
                                <FilterMenuItem
                                    label="Date"
                                    value={getDateLabel(filter.date)}
                                    open={activeFilterMenu === "date"}
                                    onClick={() =>
                                        setActiveFilterMenu(
                                            activeFilterMenu === "date" ? null : "date"
                                        )
                                    }
                                />

                                {activeFilterMenu === "date" && (
                                    <div className="mt-1 rounded-lg bg-gray-50 p-2">
                                        <FilterOption
                                            label="Today"
                                            active={filter.date === "today"}
                                            onClick={() => {
                                                setFilter(prev => ({ ...prev, date: "today" }));
                                                setDateRange({ from: "", to: "" });
                                                setActiveFilterMenu(null);
                                            }}
                                        />

                                        <FilterOption
                                            label="Yesterday"
                                            active={filter.date === "yesterday"}
                                            onClick={() => {
                                                setFilter(prev => ({
                                                    ...prev,
                                                    date: "yesterday",
                                                }));
                                                setDateRange({ from: "", to: "" });
                                                setActiveFilterMenu(null);
                                            }}
                                        />

                                        <FilterOption
                                            label="This Week"
                                            active={filter.date === "week"}
                                            onClick={() => {
                                                setFilter(prev => ({ ...prev, date: "week" }));
                                                setDateRange({ from: "", to: "" });
                                                setActiveFilterMenu(null);
                                            }}
                                        />

                                        <FilterOption
                                            label="This Month"
                                            active={filter.date === "month"}
                                            onClick={() => {
                                                setFilter(prev => ({ ...prev, date: "month" }));
                                                setDateRange({ from: "", to: "" });
                                                setActiveFilterMenu(null);
                                            }}
                                        />

                                        <FilterOption
                                            label="This Year"
                                            active={filter.date === "year"}
                                            onClick={() => {
                                                setFilter(prev => ({ ...prev, date: "year" }));
                                                setDateRange({ from: "", to: "" });
                                                setActiveFilterMenu(null);
                                            }}
                                        />

                                        {/* Custom Range */}
                                        <div className="mt-2 border-t border-gray-200 pt-2">
                                            <p className="mb-2 px-2 text-xs font-medium text-gray-500">
                                                Custom Range
                                            </p>

                                            <div className="space-y-2">
                                                <input
                                                    type="date"
                                                    value={dateRange.from}
                                                    onChange={(e) =>
                                                        setDateRange(prev => ({
                                                            ...prev,
                                                            from: e.target.value,
                                                        }))
                                                    }
                                                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500"
                                                />

                                                <input
                                                    type="date"
                                                    value={dateRange.to}
                                                    onChange={(e) =>
                                                        setDateRange(prev => ({
                                                            ...prev,
                                                            to: e.target.value,
                                                        }))
                                                    }
                                                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500"
                                                />

                                                <button
                                                    disabled={!dateRange.from || !dateRange.to}
                                                    onClick={() => {
                                                        setFilter(prev => ({
                                                            ...prev,
                                                            date: "custom",
                                                        }));
                                                        setActiveFilterMenu(null);
                                                    }}
                                                    className="w-full rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    Apply Range
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Site */}
                                <FilterMenuItem
                                    label="Site"
                                    value={filter.site === "all" ? "All Sites" : filter.site}
                                    open={activeFilterMenu === "site"}
                                    onClick={() =>
                                        setActiveFilterMenu(
                                            activeFilterMenu === "site" ? null : "site"
                                        )
                                    }
                                />

                                {activeFilterMenu === "site" && (
                                    <div className="mt-1 rounded-lg bg-gray-50 p-2">
                                        {[
                                            "all",
                                            ...sites.map(site => capitalizeFirst(site.name)),
                                        ].map(site => (
                                            <FilterOption
                                                key={site}
                                                label={site === "all" ? "All Sites" : site}
                                                active={filter.site === site}
                                                onClick={() => {
                                                    setFilter(prev => ({
                                                        ...prev,
                                                        site,
                                                    }));
                                                    setActiveFilterMenu(null);
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Clear */}
                                <div className="mt-3 flex gap-2 border-t border-gray-100 pt-3">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            // Apply filters
                                            setShowFilters(false);
                                            setActiveFilterMenu(null)
                                            getAttendance();
                                        }}
                                        className="flex-1 rounded-lg bg-indigo-600 px-3 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                                    >
                                        Apply
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFilter({
                                                date: "today",
                                                department: "all",
                                                site: "all",
                                                status: "all",
                                            });
                                            // setShowFilters(false);

                                            setDateRange({
                                                from: "",
                                                to: "",
                                            });


                                            setSearch("")
                                            setActiveFilterMenu(null);
                                        }}
                                        className="flex-1 rounded-lg bg-gray-100 px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Summary */}
            <div className="mb-6 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">

                <button
                    onClick={() => setShowSummary(!showSummary)}
                    className="flex w-full items-center justify-between border-b px-5 py-4"
                >

                    <div className="flex items-center gap-2 font-semibold text-gray-800">

                        {FaChartBar({})}

                        Attendance Summary

                    </div>

                    {showSummary ? chevUpIcon : chevDownIcon}

                </button>

                {showSummary && (

                    <div className="p-4 lg:p-5">

                        <div className="grid grid-cols-2 gap-4 xl:grid-cols-2">

                            {stats.map((card) => (

                                <div
                                    key={card.title}
                                    onClick={() => {
                                        filter.status = card.id;
                                        navigate(`/attendance/status/${card.id}`);
                                    }}
                                    className="group cursor-pointer rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-all duration-200 hover:border-blue-200 hover:shadow-md"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                {card.title}
                                            </p>

                                            <h2 className="mt-2 text-2xl font-bold lg:text-3xl">
                                                {card.value}
                                            </h2>

                                            <div className="mt-2 flex items-center gap-1 text-xs font-medium text-blue-600">
                                                <span>View attendance</span>
                                                {FaArrowRight({ className: "text-[10px] transition-transform group-hover:translate-x-1" })}
                                            </div>
                                        </div>

                                        <div
                                            className={`flex h-12 w-12 items-center justify-center rounded-full ${card.bg}`}
                                        >
                                            {card.icon}
                                        </div>
                                    </div>
                                </div>

                            ))}

                        </div>

                    </div>

                )}

            </div>


            {/* Attendance Records */}
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">

                <button
                    onClick={() => setShowAttendance(!showAttendance)}
                    className="flex w-full items-center justify-between border-b px-5 py-4"
                >

                    <div className="flex items-center gap-2 font-semibold text-gray-800">

                        {FaUsers({})}

                        Attendance Records

                    </div>

                    {showAttendance ? chevUpIcon : chevDownIcon}

                </button>

                {showAttendance && (
                    <>
                        {searchStaff?.length === 0 ? (

                            hasActiveFilters ? (
                                // No results from search/filter
                                <div className="flex min-h-[400px] items-center justify-center px-5 py-12">
                                    <div className="max-w-md text-center">

                                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-gray-100 text-gray-400">
                                            {FaSearch({ size: 30 })}
                                        </div>

                                        <h2 className="mt-6 text-xl font-bold text-gray-800 sm:text-2xl">
                                            No Results Found
                                        </h2>

                                        <p className="mt-2 text-sm leading-6 text-gray-500">
                                            We couldn't find any attendance records matching your current
                                            search or filters.
                                        </p>

                                        <button
                                            onClick={() => {
                                                const defaultFilter = {
                                                    date: "today", department: "all",
                                                    site: "all", status: "all"
                                                };


                                                setSearch("");
                                                setActiveFilterMenu(null);
                                                setFilter(defaultFilter);
                                                getAttendance(defaultFilter);
                                            }}
                                            className="mt-6 inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                                        >
                                            {FaFilterCircleXmark({})}
                                            Clear Filters
                                        </button>

                                    </div>
                                </div>
                            ) : (
                                // Nobody marked attendance
                                <div className="flex min-h-[400px] items-center justify-center px-5 py-12">
                                    <div className="max-w-md text-center">

                                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                                            {FaClipboardCheck({ size: 34 })}
                                        </div>

                                        <h2 className="mt-6 text-xl font-bold text-gray-800 sm:text-2xl">
                                            No Attendance Marked
                                        </h2>

                                        <p className="mt-2 text-sm leading-6 text-gray-500">
                                            No staff member has marked attendance yet. Attendance records
                                            will appear here once someone clocks in.
                                        </p>

                                    </div>
                                </div>
                            )

                        ) : (
                            <>
                                {/* ========= Desktop Table ================== */}

                                <div className="hidden md:block overflow-x-auto">

                                    <table className="min-w-full">

                                        <thead className="bg-gray-100">

                                            <tr className="text-left text-sm text-gray-600">

                                                {/* <th className="px-5 py-4">S/N</th> */}
                                                <th className="px-5 py-4">Staff</th>
                                                <th className="px-5 py-4">Site</th>
                                                <th className="px-5 py-4">Date</th>
                                                <th className="px-5 py-4">Clock In</th>
                                                <th className="px-5 py-4">Clock Out</th>
                                                <th className="px-5 py-4">Hours</th>
                                                <th className="px-5 py-4">Distance</th>
                                                {/* <th className="px-5 py-4">Status</th> */}
                                                <th className="px-5 py-4 text-center">Action</th>

                                            </tr>

                                        </thead>

                                        <tbody>

                                            {(searchStaff ?? []).map((staff: any, idx: number) => (

                                                <tr
                                                    key={staff.id}
                                                    className="border-b transition hover:bg-gray-50"
                                                >

                                                    {/* <td className="px-5 py-4 capitalize">
                                                        {idx + 1}
                                                    </td> */}
                                                    <td className="px-5 py-4">
                                                        <h3 className="font-semibold capitalize">
                                                            {staff.employeeName}
                                                        </h3>

                                                        <p className="text-sm text-gray-500">
                                                            {staff.employeeId}
                                                        </p>

                                                        <p className="text-xs text-gray-400 capitalize">
                                                            {staff.department}
                                                        </p>

                                                    </td>

                                                    <td className="px-5 py-4 capitalize">
                                                        {staff.site}
                                                    </td>

                                                    <td className="px-5 py-4">
                                                        {formatDate(staff.createdAt)}
                                                    </td>
                                                    <td className="px-5 py-4">
                                                        {formatTime(staff.clockIn)}
                                                    </td>



                                                    <td className="px-5 py-4">
                                                        {staff.clockOut ? formatTime(staff.clockOut) : "---"}
                                                    </td>

                                                    <td className="px-5 py-4 font-medium">
                                                        {staff.clockOut ? (
                                                            formatDuration(staff.clockIn, staff.clockOut)
                                                        ) : (
                                                            <span className="text-yellow-600">
                                                                Ongoing
                                                            </span>
                                                        )}
                                                    </td>

                                                    <td className="px-5 py-4">
                                                        {staff.distance}
                                                    </td>
                                                    <td className="px-5 py-4 text-center">

                                                        <button
                                                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
                                                            onClick={() => showAttendantDetails(staff)}
                                                        >

                                                            {FaEye({})}

                                                            View

                                                        </button>

                                                    </td>

                                                </tr>

                                            ))}

                                        </tbody>

                                    </table>

                                </div>

                                {/* ================= Mobile Cards ================== */}
                                <div className="space-y-4 p-4 lg:hidden">
                                    {searchStaff.map((staff: any) => (
                                        <div
                                            key={staff.id}
                                            className="overflow-hidden rounded-xl border border-gray-200 bg-white"
                                        >

                                            {/* Header */}

                                            <div className="flex items-start justify-between border-b p-4">

                                                <div>

                                                    <h3 className="font-semibold text-gray-800 capitalize">
                                                        {staff.employeeName}
                                                    </h3>

                                                    <p className="text-sm text-gray-500 ">
                                                        {staff.staffId}
                                                    </p>

                                                    <p className="text-xs text-gray-400 capitalize">
                                                        {staff.portfolio}
                                                    </p>

                                                </div>
                                            </div>

                                            {/* Body */}

                                            <div className="space-y-4 p-4">

                                                <div>

                                                    <p className="text-xs uppercase tracking-wide text-gray-400 capitalize">
                                                        Site
                                                    </p>

                                                    <p className="font-medium capitalize">
                                                        {staff.site}
                                                    </p>

                                                </div>

                                                <div className="grid grid-cols-2 gap-4">

                                                    <div>

                                                        <p className="text-xs text-gray-400">
                                                            Clock In
                                                        </p>

                                                        <p className="font-semibold">
                                                            {formatTime(staff.clockIn)}
                                                        </p>

                                                    </div>

                                                    <div>

                                                        <p className="text-xs text-gray-400">
                                                            Clock Out
                                                        </p>

                                                        <p className="font-semibold">
                                                            {staff.clockOut ? formatTime(staff.clockOut) : "---"}
                                                        </p>
                                                    </div>
                                                    <div>

                                                        <p className="text-xs text-gray-400">
                                                            Hours Worked
                                                        </p>

                                                        {staff.clockOut ? (
                                                            formatDuration(staff.clockIn, staff.clockOut)
                                                        ) : (
                                                            <span className="text-yellow-600">
                                                                Ongoing
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-400">
                                                            Distance
                                                        </p>
                                                        <p className="font-semibold">
                                                            {staff.distance}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Footer */}
                                            <div className="border-t p-4">
                                                <button
                                                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 py-3 font-medium text-white hover:bg-indigo-700"
                                                    onClick={() => showAttendantDetails(staff)}
                                                >
                                                    {FaEye({})}
                                                    View Attendance
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>)}
                    </>
                )}
            </div>
        </div>
    );
}